import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pageMetadata, sessionPackages, testimonials } from '../data';
import Gallery from '../components/Gallery';

export default function SessionsPage() {
  const [galleryImages, setGalleryImages] = useState([]);

  // Fetch gallery from CMS
  useEffect(() => {
    // Set page title and meta description
    document.title = pageMetadata.sessions.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', pageMetadata.sessions.description);

    const fetchGallery = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/gallery');
        if (res.ok) {
          const items = await res.json();
          // Find the gallery with slug 'sessioner-galleri'
          const sessionGallery = items.find(g => g.slug?.da === 'sessioner-galleri' || g.slug === 'sessioner-galleri');
          if (sessionGallery) {
            setGalleryImages(sessionGallery.images || []);
          }
        }
      } catch (e) {
        console.error('Error fetching gallery:', e);
      }
    };

    fetchGallery();
  }, []);

  return (
    <div className="space-y-24 pb-24">
      {/* Page Header / Intro */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center space-y-8">
        <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Nærvær & Fordybelse</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-brand-dark tracking-wide font-display">
          Individuelle sessioner – <br />
          <span className="italic text-brand-green font-medium">Skab liv i livet</span>
        </h1>
        <div className="space-y-6 text-brand-muted font-sans text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
          <p>
            I en 1:1 session stiller jeg mig til rådighed, for det du har på hjertet, som du ønsker transformeret og åbnet til en ny dimension af dig.
          </p>
          <p>
            Når du fortæller, ser jeg ind bag slør og låsninger, hvor din Livskraft ligger i dvale. Dvaletilstanden løsnes og du kan træde ind i din kraft og derfra skabe det du drømmer om i dit liv og dit virke.
          </p>
          <p className="text-sm italic text-brand-muted/80">
            Bemærk: Jeg fokuserer primært på mine events og ceremonier i dag, men tilbyder stadig et begrænset antal individuelle sessioner efter aftale.
          </p>
        </div>
        <div className="pt-4">
          <Link
            to="/kontakt"
            className="bg-brand-green hover:bg-brand-green/90 text-brand-cream px-8 py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-sm"
          >
            Kontakt for forespørgsel
          </Link>
        </div>
      </section>

      {/* Split Info Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-md border border-brand-sand">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-350x380-4.webp"
                alt="1:1 Session hos Johanne Eis"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6 text-left">
            <h2 className="text-3xl sm:text-4xl text-brand-dark tracking-wide">Livskraft med bevidsthed</h2>
            <div className="space-y-4 text-brand-muted font-sans leading-relaxed">
              <p>
                Hvis vi bevidst vælger at træde ind i vores lys i forbundethed, er vi i Lyset.
              </p>
              <p>
                Jeg kalder dig til at gå ind i det rum i dig, hvor din livskraft er og mærke kraften og dit lys der. Dig som forbundet til at gå med en ny version af dig i lys. En begyndelse til skabelsen af det du drømmer om tager form.
              </p>
            </div>
            <div className="space-y-3 pt-4">
              <h4 className="font-display text-xl text-brand-dark">En session er en blanding af:</h4>
              <ul className="space-y-2 text-brand-muted font-sans text-sm">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                  <span>Magisk transformation</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                  <span>Bevidsthedsudvidelse</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                  <span>At blive Set af det seende øje</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                  <span>Stemme og Lyd arbejde</span>
                </li>
              </ul>
              <p className="text-xs italic text-brand-muted pt-2">– samt ubeskriveligt meget mere</p>
            </div>
          </div>
        </div>
      </section>

      {/* Session Packages Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Priser & Varighed</span>
          <h2 className="text-4xl text-brand-dark tracking-wide">Vælg din session</h2>
          <p className="text-brand-muted font-sans">
            Herunder kan du se de forskellige typer af 1:1 sessioner, jeg tilbyder.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {sessionPackages.map((pkg, idx) => (
            <div 
              key={idx} 
              className="bg-brand-cream p-8 rounded-3xl border border-brand-sand shadow-sm flex flex-col justify-between h-full text-left"
            >
              <div className="space-y-6">
                <div className="space-y-2">
                  <span className="block text-xs uppercase tracking-wider text-brand-green font-semibold font-sans">
                    {pkg.duration}
                  </span>
                  <h3 className="font-display text-2xl text-brand-dark leading-snug">
                    {pkg.title}
                  </h3>
                </div>
                <p className="text-brand-muted text-sm leading-relaxed font-sans">
                  {pkg.description}
                </p>
              </div>
              <div className="pt-8 mt-8 border-t border-brand-sand flex items-center justify-between gap-4">
                <div>
                  <span className="block text-xs text-brand-muted uppercase tracking-wider font-sans">Pris</span>
                  <span className="font-display text-2xl font-semibold text-brand-dark">{pkg.price}</span>
                </div>
                <Link
                  to="/kontakt"
                  className="bg-brand-green hover:bg-brand-green/90 text-brand-cream px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 shadow-sm"
                >
                  {pkg.ctaText}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-brand-sand/20 py-20 border-y border-brand-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Erfaringer</span>
            <h2 className="text-4xl text-brand-dark tracking-wide">Hvad kunderne fortæller</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.slice(3, 6).map((item, idx) => (
              <div key={idx} className="bg-brand-cream p-8 rounded-2xl border border-brand-sand flex flex-col justify-between h-full text-left">
                <p className="text-brand-muted text-sm leading-relaxed italic mb-6 font-sans">
                  “ {item.text} ”
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-brand-sand/50">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover bg-brand-sand"
                  />
                  <span className="font-display text-base font-semibold text-brand-dark">{item.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Gallery images={galleryImages} title="Billeder fra vores rolige rum" />
        </section>
      )}
    </div>
  );
}
